import {useStore} from '@/store/root-store';
import {observer} from 'mobx-react';
import {Button} from '@/components/common-components/v2';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowRight, faCaretDown} from '@fortawesome/pro-solid-svg-icons';
import styled from 'styled-components';
import {CommonInput} from '@/components/common-components';
import {useState, useEffect} from 'react';
import {Modal, Tooltip} from 'antd';
import {CurrentPageStore} from '@/store/content-optimizer/currentPage';
import {SkeletonRingLoader} from '@/components/common-components';
import {Select, Checkbox} from 'antd';
import {notification} from '@/utils/notification';
import {ImageListComponent} from './imageListComponent';
import {QuotaInfoDetail} from '@/components/common-components/v2/quotaInfo';
import {getLocalesByKey as t} from '@/utils/translation';

interface Props {
  activeTab?: any;
  onChange: (editorState: any) => any;
  store: CurrentPageStore;
}

export const AICreateImagesTab = observer(({activeTab, onChange, store}: Props) => {
  const {contentOptimizer: {currentPage}} = useStore('');
  const {isUnsplashDrawerOpen} = currentPage;
  const [historyCopyStatus, setHistoryCopyStatus] = useState('Get URL');
  const [aspectRatio, setAspectRatio] = useState<any>('16:9');
  const [imagesAspectRatio, setImagesAspectRatio] = useState<any>('');
  // const [model, setModel] = useState<any>('Midjourney');
  const [isRealistic, setIsRealistic] = useState(false);


  const {
    contentOptimizer: {
      currentPage: {
        content,
      },
      aiOutline: {
        loadAiTemplateOutlinesHistory,
        loadAiGeneratedImage,
        setDummyImages,
        setImageGenerateInputValue,
        imageGenerateInputValue,
        loading,
        templatesHistory,
        historyLoading,
        setCopyStatus,
      },
      pages: {
        projects,
      },
    },
  } = useStore('');
  const {settings: {customer: {isGHLAccount}}} = useStore('');
  const {settings: {customer: {getCustomerQuota, setShowScaTopUpModal}}} = useStore('');
  const {settings: {customer: {profile: {isWhitelabel}}}} = useStore('');
  const {settings: {customer: {profile: {isAllowedAiContentGenerationQuotaLimitReached, quotaUtilization}}}} = useStore('');
  const {plans: {showSidebarPaymentDrawer}} = useStore('');
  const [buttonTitle, setButtonTitle] = useState('');
  const [showError, setShowError] = useState(false);
  const [showSelectedHistoryImage, setShowSelectedHistoryImage] = useState(0);
  const [showHistoryImage, setShowHistoryImage] = useState(false);
  const {Option} = Select;
  const selectSuffixIcon = <FontAwesomeIcon icon={faCaretDown} color={'#121212'}/>;

  useEffect(() => {
    if (isUnsplashDrawerOpen && activeTab == 'aiCreateImages') {
      loadAiTemplateOutlinesHistory('ai_image_generation');
      setDummyImages([], true);
    }
  }, [isUnsplashDrawerOpen]);

  useEffect(() => {
    const filteredObj = projects.find(item => item?.id === content?.projectId);
    setAspectRatio(filteredObj?.defaultAiSettings?.imagesAspectRatio || '16:9');
    setImagesAspectRatio(filteredObj?.defaultAiSettings?.imagesAspectRatio || '');
    // setModel('Midjourney');
  }, []);

  const onGenerateBtnClick = async () => {
    if (isWhitelabel && isAllowedAiContentGenerationQuotaLimitReached) {
      if (imageGenerateInputValue) {
        if (isGHLAccount) {
          notification.error('', "You've reached the limit to optimize this page. Need more? Reach out to our support team for help!");
        } else {
          notification.error('Quota is exceeded', 'You’ve exceeded the usage limit for this tool.');
        }
      } else {
        setShowError(true);
      }
    } else if (!isAllowedAiContentGenerationQuotaLimitReached) {
      if (imageGenerateInputValue) {
        try {
          setButtonTitle('generate-btn');
          setHistoryCopyStatus('Get URL');
          setCopyStatus('Copy URL');
          setDummyImages([{title: imageGenerateInputValue, load: true}], false);
          setImageGenerateInputValue('');
          await loadAiGeneratedImage(imageGenerateInputValue, aspectRatio, isRealistic, false, 'midjourney');
          setHistoryCopyStatus('Get URL');
          setCopyStatus('Copy URL');
          getCustomerQuota();
          setImageGenerateInputValue('');
        } catch (e) {
          Promise.reject(e);
        }
      } else {
        setShowError(true);
      }
    } else {
      if (imageGenerateInputValue) {
        showSidebarPaymentDrawer(null, {title: 'You need more AI quota to continue', subtitle: '', text: `Current limit: ${quotaUtilization?.ca?.allowedAiContentGeneration?.total}`});
      } else {
        setShowError(true);
      }
    }
  };

  const aspectRatioOptions = [{
    name: 'Article image (8:3)',
    value: '8:3',
  }, {
    name: 'Wide screen image (16:9)',
    value: '16:9',
  }, {
    name: 'Square image (1:1)',
    value: '1:1',
  }, {
    name: 'Vertical image (2:3)',
    value: '2:3',
  }];

  return (
    <>
      <QuotaWrapper>
        <QuotaInfoDetail
          consumedData={quotaUtilization?.ca?.allowedAiContentGeneration?.consumed}
          totalData={quotaUtilization?.ca?.allowedAiContentGeneration?.total}
          heading={t('ai-quota')}
          description={'Total number of AI Generated Images, Meta Data and “Write for me” Paragraphs'}
          ctaLabel={t('top-up-quota')}
          showCta={quotaUtilization?.ca?.allowedAiContentGeneration?.consumed >= quotaUtilization?.ca?.allowedAiContentGeneration?.total}
          customCta={() => {
            setShowScaTopUpModal(true);
          }}
          isCenter={true}/>
      </QuotaWrapper>
      <InputWrapper>
        <CommonInputStyled
          value={imageGenerateInputValue}
          placeholder='e.g “dog walking in a nice park”'
          onChange={e => setImageGenerateInputValue(e?.target?.value)}
          onFocus={() => setShowError(false)}/>
        <Tooltip title={buttonTitle == 'generate-btn' && loading ? `Image Generation started. You can submit a new request` : ''}>
          <Button
            color={'purple'}
            // loading={buttonTitle == 'generate-btn' && loading ? loading : false}
            style={{marginLeft: 0, width: 'fit-content', borderRadius: '8px'}}
            onClick={() => onGenerateBtnClick()}>
            Generate image
            <FontAwesomeIcon icon={faArrowRight} fontSize={'14px'} style={{marginLeft: '6px'}} />
          </Button>
        </Tooltip>
      </InputWrapper>
      {showError && <div
        style={{
          fontSize: '12px',
          color: 'red',
          height: '20px',
          whiteSpace: 'nowrap',
          width: '100%',
          marginLeft: '3px'}}>Please enter a prompt to generate image</div>}
      <SettingContainer>
        <SelectContainer>
          <OptionLabel>Aspect ratio:</OptionLabel>
          <SelectStyled
            grayBackground={false}
            suffixIcon={selectSuffixIcon}
            value={imagesAspectRatio || aspectRatio}
            onChange={value => {
              setImagesAspectRatio(value);
              setAspectRatio(value);
            }}>
            {aspectRatioOptions.map((item, idx) => {
              return <Option style={{fontSize: 12}} key={idx} value={item.value}>{item.name}</Option>;
            })}
          </SelectStyled>
        </SelectContainer>
        {/* <SelectContainer>
          <OptionLabel>Model:</OptionLabel>
          <CommonInputStyled
            value={model}
            placeholder='Model'
          />
        </SelectContainer> */}
        <SelectContainer>
          <OptionLabel>Type:</OptionLabel>
          <Checkbox autoFocus={true} defaultChecked={false} checked={isRealistic} onChange={e=>setIsRealistic(e.target.checked)}>Generate a photo realistic image</Checkbox>
        </SelectContainer>
      </SettingContainer>
      <div style={{marginTop: '26px', marginBottom: '10px', fontSize: '14px', fontWeight: '400'}}>History</div>
      {historyLoading ? <div style={{display: 'flex', justifyContent: 'center'}}><SkeletonRingLoader/></div> :
        <ImageListComponent onChange={onChange} store={store} historyCopyStatus={historyCopyStatus} buttonTitle={buttonTitle} setShowSelectedHistoryImage={setShowSelectedHistoryImage} setShowHistoryImage={setShowHistoryImage} />
      }
      <ModalImage
        title=''
        centered
        footer={<></>}
        className='openImage'
        visible={showHistoryImage}
        onOk={() => setShowHistoryImage(false)}
        onCancel={() => setShowHistoryImage(false)}
      >
        {templatesHistory && <img height={600} src={templatesHistory[showSelectedHistoryImage]?.result} alt='' />}
      </ModalImage>
    </>
  );
});

const QuotaWrapper = styled.div`
  margin-top: 10px;
  margin-left: 4px;
`;

const InputWrapper = styled.div`
  display: flex;
  margin-top: 10px;
`;

const ModalImage = styled(Modal)`
  width: auto !important;
  .ant-modal-content{
    background-color: transparent !important;
    .ant-modal-body{
      padding: 0 !important;
    }
  }
  .ant-modal-footer{
    padding: 0;
    border-top: 0;
  }
  .ant-modal-close{
    right: -45px;
    top: -35px;
    path{
      fill:white
    }
  }
`;

const CommonInputStyled = styled(CommonInput)`
  max-width: 520px;
  height: 44px !important;
  margin-right: 10px !important;
  &input {
    border-radius: 5px;
    border: 1px solid #E8E8E8;
  }
`;

const SettingContainer = styled.div`
  margin: 15px 10px;
`;

const SelectContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 6px;
  font-size: 12px;
`;

const OptionLabel = styled.div`
  margin-right: 20px;
  min-width: 110px;
  font-size: 12px;
`;

const SelectStyled = styled(Select)<{grayBackground?: boolean}>`
  .ant-select-selector {
    border-radius: 6px !important;
    border: 1px solid #E8E8E8 !important;
    background-color: ${p => p.grayBackground ? '#F9F9FB': '#fff'} !important;
    box-shadow: 0px 3px 2px 0px rgba(0, 0, 0, 0.02) !important;
    min-width: 180px;
    font-size: 12px;
  }
  .ant-select-item {
    font-size: 12px !important;
  }
`;
